import React from 'react';
import { Stack } from '@mui/material';

import { Direction2D, PointSlopeLine } from '@paragon/contract-library';
import { degreesToVector, vectorToDegrees } from '@paragon/geometry-library';
import { AngleField, DistanceField } from '@paragon/material';

import { findAndSetReferenceLine } from '../../../../store/actions/editorActions';
import { startSetReferenceLineIntersection } from '../../../../store/actions/insertActions';
import { getAsyncRequestForActiveComponentDesign } from '../../../../store/selectors/getAsyncRequestForActiveComponentDesignSelector';
import { getReferenceLineLongNamesByGuidForActiveComponent } from '../../../../store/selectors/getReferenceLineLongNamesByGuidForActiveComponentSelector';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import ReferenceLineIntersectionProperty from '../elements/ReferenceLineIntersectionProperty';
import ReferenceLineNameProperty from '../elements/ReferenceLineNameProperty';
import { ReferenceLineTypeProperty } from '../elements/ReferenceLineTypeProperty';

interface Props {
  line: PointSlopeLine;
  afterUpdateLine?: () => void;
}

const PointSlopeLineProperties = ({ line, afterUpdateLine }: Props) => {
  const dispatch = useAppDispatch();
  const referenceLineLongNamesByGuid = useAppSelector(getReferenceLineLongNamesByGuidForActiveComponent);
  const disabled = useAppSelector((state) => {
    const asyncRequest = getAsyncRequestForActiveComponentDesign(state);
    return propertiesShouldBeViewOnly(state) || (!!asyncRequest && asyncRequest.type !== 'updateFeatures');
  });

  const updateLine = async (newLine: PointSlopeLine) => {
    await dispatch(findAndSetReferenceLine(newLine));
    afterUpdateLine && afterUpdateLine();
  };

  const updateDirection = async (newDirection: Direction2D) => await updateLine({ ...line, direction: newDirection });

  const onApplyOffset = async (newOffset: number) => await updateLine({ ...line, offset: newOffset });

  if (!referenceLineLongNamesByGuid) return null;

  return (
    <Stack direction="column" spacing={2}>
      <ReferenceLineNameProperty sx={{ marginBottom: '0!important' }} line={line} disabled={disabled} />
      <ReferenceLineTypeProperty line={line} disabled={disabled} />

      <ReferenceLineIntersectionProperty
        disabled={disabled}
        line={line}
        property="Defining Intersection"
        lineKeys={{ key1: 'line1Guid', key2: 'line2Guid' }}
        referenceLineLongNamesByGuid={referenceLineLongNamesByGuid}
        onStartSelectingIntersection={(referenceLine, key1, key2) =>
          dispatch(startSetReferenceLineIntersection(referenceLine, key1, key2))
        }
      />

      <AngleField
        defaultDisplayFormat="slope"
        value={vectorToDegrees(line.direction)}
        label="Direction"
        disabled={disabled}
        onSubmit={(newDirection) => updateDirection(degreesToVector(newDirection))}
      />

      <DistanceField value={line.offset} label="Offset" onSubmit={onApplyOffset} disabled={disabled} />
    </Stack>
  );
};

export default PointSlopeLineProperties;
